// NameMeaningCalculator.jsx
import React, { useState } from 'react';
import { Helmet } from 'react-helmet';
import { motion } from 'framer-motion';
import { Bar } from 'react-chartjs-2';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  BarElement,
  Title,
  Tooltip,
  Legend
} from 'chart.js';

ChartJS.register(
  CategoryScale,
  LinearScale,
  BarElement,
  Title,
  Tooltip,
  Legend
);

const NameMeaningCalculator = () => {
  const [nameData, setNameData] = useState({
    name: '',
    birthDate: '',
    gender: 'any'
  });

  const [results, setResults] = useState(null);
  const [error, setError] = useState('');
  const [chartData, setChartData] = useState(null);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setNameData(prev => ({
      ...prev,
      [name]: value
    }));
    setError('');
  };

  const calculateNameMeaning = (e) => {
    e.preventDefault(); // Prevent the default form submission behavior

    const { name, birthDate, gender } = nameData;
    
    if (!name) {
      setError('Please enter a name');
      return;
    }

    // Mock calculation logic
    const nameAnalysis = {
      numerologyNumber: calculateNumerology(name),
      personalityTraits: getPersonalityTraits(name),
      luckyNumbers: getLuckyNumbers(name),
      compatibilityScore: calculateCompatibility(name, birthDate)
    };

    const newResults = {
      ...nameAnalysis,
      calculationSteps: [
        'Step 1: Numerology Calculation',
        `Sum of name letters: ${nameAnalysis.numerologyNumber}`,
        '\nStep 2: Personality Traits Analysis',
        `Traits: ${nameAnalysis.personalityTraits.join(', ')}`,
        '\nStep 3: Lucky Numbers',
        `Lucky numbers: ${nameAnalysis.luckyNumbers.join(', ')}`,
        '\nStep 4: Compatibility Score',
        `Compatibility: ${nameAnalysis.compatibilityScore}%`
      ]
    };

    setResults(newResults);

    // Update chart data
    setChartData({
      labels: ['Personality', 'Compatibility', 'Lucky Numbers'],
      datasets: [{
        label: 'Analysis Results',
        data: [
          nameAnalysis.personalityTraits.length,
          nameAnalysis.compatibilityScore,
          nameAnalysis.luckyNumbers.length
        ],
        backgroundColor: ['rgba(75, 192, 192, 0.5)', 'rgba(54, 162, 235, 0.5)', 'rgba(255, 99, 132, 0.5)'],
        borderColor: ['rgba(75, 192, 192, 1)', 'rgba(54, 162, 235, 1)', 'rgba(255, 99, 132, 1)'],
        borderWidth: 1
      }]
    });
  };

  const calculateNumerology = (name) => {
    const letterValues = {
      A: 1, J: 1, S: 1,
      B: 2, K: 2, T: 2,
      C: 3, L: 3, U: 3,
      D: 4, M: 4, V: 4,
      E: 5, N: 5, W: 5,
      F: 6, O: 6, X: 6,
      G: 7, P: 7, Y: 7,
      H: 8, Q: 8, Z: 8,
      I: 9, R: 9
    };

    const sum = name.toUpperCase().split('').reduce((acc, letter) => {
      return acc + (letterValues[letter] || 0);
    }, 0);

    return sum % 9 || 9;
  };

  const getPersonalityTraits = (name) => {
    const traits = [
      'Creative', 'Intuitive', 'Expressive',
      'Sensitive', 'Artistic', 'Emotional'
    ];
    return traits.slice(0, Math.floor(Math.random() * traits.length) + 2);
  };

  const getLuckyNumbers = (name) => {
    const num = calculateNumerology(name);
    return [num, num * 2, num * 3];
  };

  const calculateCompatibility = (name, birthDate) => {
    const baseScore = calculateNumerology(name);
    return Math.floor((baseScore * 10) + Math.random() * 10);
  };

  return (
    <>
      <Helmet>
        <title>Name Meaning Calculator | Analyze Your Name's Numerology</title>
        <meta name="description" content="Calculate the numerological meaning of your name, personality traits, lucky numbers, and compatibility scores with our Name Meaning Calculator." />
        <meta name="keywords" content="name meaning, numerology calculator, personality traits, lucky numbers, compatibility score, name analysis" />
        <meta property="og:title" content="Name Meaning Calculator | Analyze Your Name's Numerology" />
        <meta property="og:description" content="Calculate the numerological meaning of your name, personality traits, lucky numbers, and compatibility scores with our Name Meaning Calculator." />
        <meta property="og:image" content="https://yourdomain.com/path/to/image.jpg" />
        <meta property="og:url" content="https://yourdomain.com/name-meaning-calculator" />
        <meta property="og:type" content="website" />
      </Helmet>

      <div className="min-h-screen bg-gray-50 py-8">
        <div className="container mx-auto px-4 max-w-7xl">
          <header className="mb-8 text-center">
            <div className="text-4xl font-bold text-purple-600 mb-2">🌟</div>
            <h1 className="text-3xl md:text-4xl font-bold text-gray-800 mb-2">Name Meaning Calculator</h1>
            <p className="text-gray-600 max-w-2xl mx-auto">
              Discover the numerological meaning of your name, personality traits, and compatibility scores.
            </p>
          </header>

          <div className="bg-white p-6 rounded-lg shadow-md max-w-2xl mx-auto">
            <form className="space-y-6" onSubmit={calculateNameMeaning}>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Name</label>
                <input
                  type="text"
                  name="name"
                  value={nameData.name}
                  onChange={handleInputChange}
                  className="w-full px-4 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-purple-500"
                  placeholder="Enter your name"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Birth Date</label>
                <input
                  type="date"
                  name="birthDate"
                  value={nameData.birthDate}
                  onChange={handleInputChange}
                  className="w-full px-4 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-purple-500"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Gender</label>
                <select
                  name="gender"
                  value={nameData.gender}
                  onChange={handleInputChange}
                  className="w-full px-4 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-purple-500"
                >
                  <option value="any">Any</option>
                  <option value="male">Male</option>
                  <option value="female">Female</option>
                </select>
              </div>

              <button
                type="submit"
                className="w-full bg-purple-600 text-white py-3 px-4 rounded-md font-semibold hover:bg-purple-700 transition duration-200"
              >
                Calculate Name Meaning
              </button>

              {error && (
                <div className="bg-red-100 text-red-700 p-4 rounded-md mt-4">
                  {error}
                </div>
              )}

              {results && (
                <div className="mt-8">
                  <h2 className="text-2xl font-bold text-gray-800 mb-6">Analysis Results</h2>
                  
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-8">
                    <div className="bg-gray-50 p-6 rounded-lg shadow-sm">
                      <h3 className="text-xl font-semibold mb-2">Numerology Number</h3>
                      <p className="text-lg font-medium">{results.numerologyNumber}</p>
                    </div>

                    <div className="bg-gray-50 p-6 rounded-lg shadow-sm">
                      <h3 className="text-xl font-semibold mb-2">Personality Traits</h3>
                      <ul className="list-disc pl-6 space-y-1">
                        {results.personalityTraits.map((trait, index) => (
                          <li key={index} className="text-gray-700">{trait}</li>
                        ))}
                      </ul>
                    </div>

                    <div className="bg-gray-50 p-6 rounded-lg shadow-sm">
                      <h3 className="text-xl font-semibold mb-2">Lucky Numbers</h3>
                      <p className="text-lg font-medium">{results.luckyNumbers.join(', ')}</p>
                    </div>

                    <div className="bg-gray-50 p-6 rounded-lg shadow-sm">
                      <h3 className="text-xl font-semibold mb-2">Compatibility Score</h3>
                      <p className="text-lg font-medium">{results.compatibilityScore}%</p>
                    </div>
                  </div>

                  <div className="bg-white p-6 rounded-lg shadow-md mb-8">
                    <h3 className="text-xl font-semibold mb-4">Analysis Visualization</h3>
                    <div className="h-96">
                      <Bar
                        data={chartData}
                        options={{
                          responsive: true,
                          maintainAspectRatio: false,
                          scales: {
                            y: {
                              beginAtZero: true,
                              title: { display: true, text: 'Analysis Score' }
                            },
                            x: { title: { display: true, text: 'Analysis Type' } }
                          },
                          plugins: { title: { display: true, text: 'Name Analysis Results' } }
                        }}
                      />
                    </div>
                  </div>

                  <div className="bg-gray-50 p-6 rounded-lg shadow-sm">
                    <h3 className="text-xl font-semibold mb-4">Calculation Steps</h3>
                    <div className="space-y-4">
                      {results.calculationSteps.map((step, index) => (
                        <div key={index} className="p-4 bg-white rounded-lg shadow-inner">
                          <pre className="whitespace-pre-wrap break-words font-mono text-sm">
                            {step}
                          </pre>
                        </div>
                      ))}
                    </div>
                  </div>
                </div>
              )}
            </form>
          </div>

          <div className="bg-white p-6 rounded-lg shadow-md max-w-2xl mx-auto mt-8">
            <h2 className="text-2xl font-bold text-gray-800 mb-6">How to Use</h2>
            <p className="text-gray-700">
              To use the Name Meaning Calculator, follow these simple steps:
            </p>
            <ol className="list-decimal pl-6 space-y-2">
              <li>Enter your name in the "Name" field.</li>
              <li>Select your birth date using the "Birth Date" field.</li>
              <li>Choose your gender from the "Gender" dropdown menu.</li>
              <li>Click the "Calculate Name Meaning" button.</li>
              <li>View the analysis results, including numerology number, personality traits, lucky numbers, and compatibility score.</li>
            </ol>
          </div>

          <div className="bg-white p-6 rounded-lg shadow-md max-w-2xl mx-auto mt-8">
            <h2 className="text-2xl font-bold text-gray-800 mb-6">Full FAQ</h2>
            <div className="space-y-4">
              <div>
                <h3 className="text-xl font-semibold text-gray-800">What is numerology?</h3>
                <p className="text-gray-700">
                  Numerology is the study of the mystical relationship between numbers and events. It is often used to gain insights into personality traits and future events.
                </p>
              </div>
              <div>
                <h3 className="text-xl font-semibold text-gray-800">How is the numerology number calculated?</h3>
                <p className="text-gray-700">
                  The numerology number is calculated by summing the numerical values of the letters in your name and reducing it to a single digit.
                </p>
              </div>
              <div>
                <h3 className="text-xl font-semibold text-gray-800">What are personality traits?</h3>
                <p className="text-gray-700">
                  Personality traits are characteristics that define your behavior and attitudes. This calculator provides a random selection of traits based on your name.
                </p>
              </div>
              <div>
                <h3 className="text-xl font-semibold text-gray-800">How are lucky numbers determined?</h3>
                <p className="text-gray-700">
                  Lucky numbers are derived from your numerology number and are multiples of that number.
                </p>
              </div>
              <div>
                <h3 className="text-xl font-semibold text-gray-800">What is the compatibility score?</h3>
                <p className="text-gray-700">
                  The compatibility score is a percentage that indicates how well you might get along with others based on your numerology number and birth date.
                </p>
              </div>
            </div>
          </div>

          <div className="bg-white p-6 rounded-lg shadow-md max-w-2xl mx-auto mt-8">
            <h2 className="text-2xl font-bold text-gray-800 mb-6">Features & Benefits</h2>
            <div className="space-y-4">
              <div>
                <h3 className="text-xl font-semibold text-gray-800">Comprehensive Analysis</h3>
                <p className="text-gray-700">
                  Get a detailed analysis of your name, including numerology, personality traits, lucky numbers, and compatibility scores.
                </p>
              </div>
              <div>
                <h3 className="text-xl font-semibold text-gray-800">Visualization</h3>
                <p className="text-gray-700">
                  Visualize your analysis results with an interactive bar chart.
                </p>
              </div>
              <div>
                <h3 className="text-xl font-semibold text-gray-800">User-Friendly Interface</h3>
                <p className="text-gray-700">
                  Easy-to-use interface with clear instructions and intuitive design.
                </p>
              </div>
              <div>
                <h3 className="text-xl font-semibold text-gray-800">Randomized Traits</h3>
                <p className="text-gray-700">
                  Random selection of personality traits to add an element of surprise and fun.
                </p>
              </div>
              <div>
                <h3 className="text-xl font-semibold text-gray-800">Compatibility Insights</h3>
                <p className="text-gray-700">
                  Gain insights into your compatibility with others based on numerology.
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default NameMeaningCalculator;